import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { noteTypesManagement } from "../../../../../../hooks/api/NoteTypesManagement";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";

const NoteTypes = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 10;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedNoteType, setSelectedNoteType] = useState([]);
    const [noteTypes, setNoteTypes] = useState();
    const { useNoteTypesListMutation, useDeleteNoteTypesMutation } = noteTypesManagement;
    const [noteTypeList, { isLoading }] = useNoteTypesListMutation();
    const [deleteNoteType] = useDeleteNoteTypesMutation();

    const tabs = [
        { title: t("noteTypes.title"), isActive: true },
        { title: t("guardianTypes.title"), onClick: () => gotoGuardianType() },
        { title: t("municipalities.title"), onClick: () => gotoMunicipalities() },
    ];

    const gotoGuardianType = () => {
        navigate("/settings/default/guardian-types");
    };

    const gotoMunicipalities = () => {
        navigate("/settings/default/municipalities");
    };

    const gotoNew = () => {
        navigate("/settings/default/note-types/add");
    };

    const onPageChange = (currentPage, startPage, limitPage) => {
        setCurrentPage(currentPage);
        setFilters({
            ...filters,
            startPage: startPage,
            limitPage: limitPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedNoteType((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedNoteType?.length; i++) {
                            const noteTypeId = selectedNoteType[i];
                            deleteNoteType(noteTypeId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    noteTypeList(filters).then((result) => {
                                        const noteTypes = result?.data?.records;
                                        setNoteTypes(noteTypes);
                                        setTotalRecords(result?.data?.totalRecords);
                                        setDisableDelete(true);
                                        onSelectionChange(noteTypeId);
                                    });
                                    toast.info(result?.data?.info?.responseMessage);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedNoteType.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedNoteType]);

    useEffect(() => {
        noteTypeList(filters).then((result) => {
            const noteTypes = result?.data?.records;
            setNoteTypes(noteTypes);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [filters, noteTypeList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("noteTypes.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("noteTypes.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("settings.default")} />
                    <Link title={t("noteTypes.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader></ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("noteTypes.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("noteTypes.color")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {noteTypes?.length > 0 &&
                                        noteTypes?.map((noteType, index) => {
                                            return (
                                                <TableRow key={noteType.id}>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={noteType.id} onChange={() => onSelectionChange(noteType.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{noteType.name}</TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ backgroundColor: noteType?.color, padding: "2px 10px", borderRadius: "20%" }}></span>
                                                        &nbsp;
                                                        <span>{noteType?.color}</span>
                                                    </TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default NoteTypes;
